import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import {
  subscribeToPushNotifications,
  unsubscribeFromPushNotifications,
} from '@/api/channel-subscription';
import { pushVapidPublicKey } from '@/config/app';
import { useAuthStore } from '@/store/auth';

export const usePushNotificationStore = defineStore(
  'push-notifications',
  () => {
    // State
    const isSupported = ref(false);
    const isSubscribed = ref(false);
    const permission = ref('default');
    const isLoading = ref(false);
    const error = ref(null);
    const swRegistration = ref(null);
    const pushSubscription = ref(null);
    const isInitialized = ref(false);

    // Getters
    const canRequestPermission = computed(() => {
      return isSupported.value && permission.value === 'default';
    });

    // Actions
    const checkSupport = () => {
      isSupported.value =
        'serviceWorker' in navigator &&
        'PushManager' in window &&
        'Notification' in window;
      return isSupported.value;
    };

    const updatePermission = () => {
      permission.value = Notification?.permission || 'default';
    };

    const initialize = async () => {
      if (isInitialized.value) return;

      try {
        isLoading.value = true;
        error.value = null;

        // Check support
        if (!checkSupport()) {
          console.warn('Push notifications not supported');
          return;
        }

        // Update permission status
        updatePermission();

        // Register service worker
        await registerServiceWorker();

        // Check current subscription
        await updateSubscriptionStatus();

        isInitialized.value = true;
        console.log('🔔 Push notification store initialized');
      } catch (err) {
        error.value = err.message;
        console.error('Failed to initialize push notifications:', err);
      } finally {
        isLoading.value = false;
      }
    };

    const registerServiceWorker = async () => {
      try {
        swRegistration.value = await navigator.serviceWorker.register(
          '/sw-push.js',
          {
            scope: '/',
          }
        );

        console.log('✅ Service worker registered');

        // Listen for updates
        swRegistration.value.addEventListener('updatefound', () => {
          console.log('🔄 Service worker update found');
        });
      } catch (err) {
        console.error('❌ Service worker registration failed:', err);
        throw new Error('Service worker registration failed');
      }
    };

    const updateSubscriptionStatus = async () => {
      try {
        if (!swRegistration.value) return;

        pushSubscription.value =
          await swRegistration.value.pushManager.getSubscription();
        isSubscribed.value = pushSubscription.value !== null;

        console.log('📱 Subscription status:', isSubscribed.value);
      } catch (err) {
        console.error('❌ Failed to update subscription status:', err);
      }
    };

    const requestPermission = async () => {
      try {
        if (!isSupported.value) {
          throw new Error('Push notifications not supported');
        }

        isLoading.value = true;

        const newPermission = await Notification.requestPermission();
        permission.value = newPermission;

        if (newPermission === 'granted') {
          console.log('✅ Notification permission granted');
          return true;
        } else {
          throw new Error('Notification permission denied');
        }
      } catch (err) {
        error.value = err.message;
        console.error('❌ Permission request failed:', err);
        throw err;
      } finally {
        isLoading.value = false;
      }
    };

    const subscribe = async () => {
      try {
        if (!isSupported.value) {
          throw new Error('Push notifications not supported');
        }

        isLoading.value = true;
        error.value = null;

        // Ensure we have permission
        if (permission.value !== 'granted') {
          await requestPermission();
        }

        // Convert VAPID key
        const applicationServerKey = urlBase64ToUint8Array(pushVapidPublicKey);

        // Subscribe to push manager
        pushSubscription.value =
          await swRegistration.value.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: applicationServerKey,
          });

        console.log('✅ Push subscription created');

        // Send to server
        await sendSubscriptionToServer(pushSubscription.value);

        isSubscribed.value = true;
        console.log('🎉 Successfully subscribed to push notifications');

        return true;
      } catch (err) {
        error.value = err.message;
        console.error('❌ Subscription failed:', err);
        throw err;
      } finally {
        isLoading.value = false;
      }
    };

    const unsubscribe = async () => {
      try {
        if (!pushSubscription.value) {
          console.log('No subscription to unsubscribe from');
          return true;
        }

        isLoading.value = true;
        error.value = null;

        // Unsubscribe from push manager
        await pushSubscription.value.unsubscribe();

        // Remove from server
        await removeSubscriptionFromServer(pushSubscription.value);

        pushSubscription.value = null;
        isSubscribed.value = false;

        console.log('✅ Successfully unsubscribed from push notifications');
        return true;
      } catch (err) {
        error.value = err.message;
        console.error('❌ Unsubscribe failed:', err);
        throw err;
      } finally {
        isLoading.value = false;
      }
    }; // Helper functions
    const sendSubscriptionToServer = async (subscription) => {
      try {
        const subscriptionData = {
          endpoint: subscription.endpoint,
          keys: {
            p256dh: arrayBufferToBase64(subscription.getKey('p256dh')),
            auth: arrayBufferToBase64(subscription.getKey('auth')),
          },
        };

        await subscribeToPushNotifications(subscriptionData);

        // Update auth store user opt_in_web to 1
        const authStore = useAuthStore();
        if (authStore.user) {
          authStore.user.opt_in_web = 1;
        }

        console.log('✅ Subscription sent to server');
      } catch (err) {
        console.error('❌ Failed to send subscription to server:', err);
        throw err;
      }
    };

    const removeSubscriptionFromServer = async (subscription) => {
      try {
        const subscriptionData = {
          endpoint: subscription.endpoint,
        };

        await unsubscribeFromPushNotifications(subscriptionData);

        // Update auth store user opt_in_web to 0
        const authStore = useAuthStore();
        if (authStore.user) {
          authStore.user.opt_in_web = 0;
        }

        console.log('✅ Subscription removed from server');
      } catch (err) {
        console.error('❌ Failed to remove subscription from server:', err);
        throw err;
      }
    };

    const urlBase64ToUint8Array = (base64String) => {
      const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
      const base64 = (base64String + padding)
        .replace(/-/g, '+')
        .replace(/_/g, '/');
      const rawData = window.atob(base64);
      const outputArray = new Uint8Array(rawData.length);

      for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
      }
      return outputArray;
    };

    const arrayBufferToBase64 = (buffer) => {
      const binary = String.fromCharCode.apply(null, new Uint8Array(buffer));
      return window.btoa(binary);
    };

    // Auto-initialize when user logs in
    const initializeAfterLogin = async () => {
      console.log('🚀 Initializing push notifications after login...');
      await initialize();

      // Auto-request permission if supported but not yet decided
      if (canRequestPermission.value) {
        console.log('🔔 Auto-requesting notification permission...');
        try {
          await requestPermission();

          // If permission granted, auto-subscribe
          if (permission.value === 'granted' && !isSubscribed.value) {
            console.log('🔔 Auto-subscribing to push notifications...');
            await subscribe();
          }
        } catch (err) {
          console.log('User declined notification permission');
        }
      }
    };

    return {
      // State
      isSupported,
      isSubscribed,
      permission,
      isLoading,
      error,
      isInitialized,

      // Getters
      canRequestPermission,

      // Actions
      initialize,
      requestPermission,
      subscribe,
      unsubscribe,
      updateSubscriptionStatus,
      initializeAfterLogin,
    };
  }
);
